import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ProductSpecialFeatureDto } from '../../../product-picker-controls/models/product-special-feature-dto.model';
import { FormApplication } from '../models/form-application.model';
import { Product } from './../../../lib/models/api/product.model';

@Injectable()
export class FormProductService {
  private allSpecialFeatures$: Observable<Array<ProductSpecialFeatureDto>>;
  constructor(private httpClient: HttpClient) {}

  public getProductByProductCode(application: FormApplication, productCode: string): Observable<Product> {
    let params = new HttpParams()
      .set('caseNumber', application.ApplicationCase.CaseNumberAsHash)
      .append('productCode', productCode.trim())
      .append('loanGroupTypeCode', application.ApplicationCase.LoanGroupTypeCode);

    if (application.EPCRating || application.PropertyEPCRatingCode) {
      params = params.append('epcRating', application.EPCRating || application.PropertyEPCRatingCode);
    }

    params = this.appendValueToHttpParams('IsKfi', application.IsKfi, params);
    params = this.appendValueToHttpParams('IsAip', application.IsAip, params);
    params = this.appendValueToHttpParams('IsFullApp', application.IsFullApp, params);

    return this.httpClient
      .get('/ApplicationCase/GetProductsByProductCode', { params })
      .pipe(map((data: Array<any>) => (data && data.length ? new Product().deserialize(data[0]) : undefined)));
  }

  public getProductByCodeIncludingExpired(application: FormApplication, productCode: string): Observable<Product> {
    const params = new HttpParams()
      .set('caseNumber', application.ApplicationCase.CaseNumberAsHash)
      .append('productCode', productCode);

    return this.httpClient
      .get('/ApplicationCase/GetAllProductsByProductCode', { params })
      .pipe(map((data: Array<any>) => (data && data.length ? new Product().deserialize(data[0]) : undefined)));
  }

  public getProductsByLoanType(
    application: FormApplication,
    loanType: string,
    epcRatingCode?: string
  ): Observable<Array<Product>> {
    let params = new HttpParams()
      .set('caseNumber', application.ApplicationCase.CaseNumberAsHash)
      .append('loanTypeCode', loanType);

    if (epcRatingCode) {
      params = params.append('epcRating', epcRatingCode);
    }

    params = this.appendValueToHttpParams('IsKfi', application.IsKfi, params);
    params = this.appendValueToHttpParams('IsAip', application.IsAip, params);
    params = this.appendValueToHttpParams('IsFullApp', application.IsFullApp, params);

    return this.httpClient
      .get('/ApplicationCase/GetProductsByLoanType', { params })
      .pipe(map((data: Array<any>) => data.map((prod) => new Product().deserialize(prod))));
  }

  private appendValueToHttpParams(valueName: string, value: boolean, params: HttpParams): HttpParams {
    return value !== undefined ? params.append(valueName, value.toString()) : params;
  }

  public getProductsSpecialFeature(): Observable<Array<ProductSpecialFeatureDto>> {
    if (!this.allSpecialFeatures$) {
      this.allSpecialFeatures$ = this.httpClient.get('/Products/GetProductsSpecialFeature').pipe(
        map((data: Array<any>) => data.map((prod) => new ProductSpecialFeatureDto().deserialize(prod))),
        shareReplay(1)
      );
    }

    return this.allSpecialFeatures$;
  }

  public getProductExpirationWarningInterval(caseNumber: string): Observable<number> {
    const params = new HttpParams().set('caseNumber', caseNumber);

    return this.httpClient
      .get('/configuration/productExpirationWarningInterval', { params })
      .pipe(map((data: number) => data));
  }
}
